import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { NotificationCategory } from "@reactive-resume/dto";
import { cn } from "@reactive-resume/utils";

import {
  markNotificationReadInCaches,
  useMarkAllNotificationsAsRead,
  useNotifications,
} from "./queries";
import { CATEGORY_LABELS } from "./event-display";
import {
  NotificationRow,
  NotificationsEmptyState,
  NotificationsSkeleton,
  groupByDay,
} from "./notification-center";
import type { NotificationItem } from "./api";

const CATEGORY_FILTERS: (NotificationCategory | "all")[] = [
  "all",
  "finance",
  "transactional",
  "referral",
  "ai",
  "account",
  "social",
  "system",
  "campaign",
];

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PAGE NOTIFICATIONS — boîte complète avec filtres par catégorie
 * ─────────────────────────────────────────────────────────────────────────────
 *  Design épuré (réf. Stripe / Linear) : typographie hiérarchique, cartes
 *  douces groupées par jour, états de charge soignés, animations discrètes.
 *  Réutilisable dans l'espace client ET l'espace admin.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const NotificationsPage = () => {
  const [category, setCategory] = useState<NotificationCategory | "all">("all");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const filters = useMemo(() => (category === "all" ? {} : { category }), [category]);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNotifications(filters);
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const notifications = data?.pages.flatMap((page) => page.items) ?? [];
  const groups = groupByDay(notifications);
  const unreadCount = notifications.filter((n: NotificationItem) => !n.isRead).length;

  const handleClick = (notification: NotificationItem) => {
    // Marquage optimiste immédiat (liste + badge) puis ouverture de la
    // lecture complète — le serveur confirme (GET /:id marque comme lue).
    markNotificationReadInCaches(queryClient, notification.id);
    navigate(`/client/user-space/notifications/${notification.id}`);
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-24 pt-10 sm:px-6">
      {/* ── En-tête ─────────────────────────────────────────────────────────── */}
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text">Notifications</h1>
          <p className="mt-1.5 text-sm text-text-muted">
            {unreadCount > 0
              ? `${unreadCount} notification${unreadCount > 1 ? "s" : ""} non lue${unreadCount > 1 ? "s" : ""}`
              : "Vous êtes à jour"}
          </p>
        </div>

        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <button
                type="button"
                onClick={() => markAllAsRead.mutate()}
                disabled={markAllAsRead.isPending}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[hsl(var(--product-brand))] transition-colors hover:bg-canvas-subtle disabled:opacity-50"
              >
                {markAllAsRead.isPending ? "Marquage…" : "Tout marquer comme lu"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── Filtres par catégorie ───────────────────────────────────────────── */}
      <div
        className="mb-6 flex flex-wrap gap-1.5"
        role="tablist"
        aria-label="Filtrer par catégorie"
      >
        {CATEGORY_FILTERS.map((value) => {
          const active = category === value;
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setCategory(value)}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-200",
                active
                  ? "bg-text text-canvas shadow-sm"
                  : "text-text-muted hover:bg-canvas-subtle hover:text-text",
              )}
            >
              {value === "all" ? "Toutes" : CATEGORY_LABELS[value]}
            </button>
          );
        })}
      </div>

      {/* ── Liste groupée par jour ──────────────────────────────────────────── */}
      {isLoading ? (
        <NotificationsSkeleton />
      ) : notifications.length === 0 ? (
        <NotificationsEmptyState filtered={category !== "all"} />
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map(([day, items], groupIndex) => (
            <motion.section
              key={day}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay: Math.min(groupIndex * 0.05, 0.25),
                ease: "easeOut",
              }}
            >
              <h2 className="mb-2 pl-1 text-[11px] font-bold uppercase tracking-[0.12em] text-text-muted">
                {day}
              </h2>
              <div className="divide-y divide-border-subtle/70 overflow-hidden rounded-2xl border border-border-subtle bg-surface shadow-[0_1px_3px_rgba(20,14,44,0.04)]">
                {items.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onClick={handleClick}
                  />
                ))}
              </div>
            </motion.section>
          ))}

          {hasNextPage && (
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="mx-auto block rounded-lg px-4 py-2 text-xs font-semibold text-text-muted transition-colors hover:bg-canvas-subtle hover:text-text disabled:opacity-50"
            >
              {isFetchingNextPage ? "Chargement…" : "Charger les plus anciennes"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
